import { Control, FieldValues, Path } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { OptionInterface } from '@/constants/options';
import { cn } from '@/lib/ui/utils';

type FormCustomRadioGroupProps<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  control: Control<T>;
  options: OptionInterface[];
  disabled?: boolean;
  className?: string;
};

const FormCustomRadioGroup = <T extends FieldValues>({
  name,
  label,
  control,
  options,
  disabled,
  className,
}: FormCustomRadioGroupProps<T>) => {
  const normalizeValue = (value: unknown): string | undefined => {
    if (Array.isArray(value)) {
      return value[0] as string;
    }
    return value as string | undefined;
  };

  const findMatchingValue = (value: unknown): string | undefined => {
    const normalized = normalizeValue(value);
    if (!normalized) return undefined;

    const exactMatch = options.find((opt) => opt.value === normalized);
    if (exactMatch) return exactMatch.value;

    const normalizedLower = normalized.toLowerCase();
    const partialMatch = options.find(
      (opt) =>
        opt.value.toLowerCase().includes(normalizedLower) ||
        normalizedLower.includes(opt.value.toLowerCase()) ||
        opt.label.toLowerCase().includes(normalizedLower) ||
        normalizedLower.includes(opt.label.toLowerCase())
    );

    return partialMatch?.value;
  };

  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => {
        const currentValue = findMatchingValue(field.value);

        return (
          <FormItem className={className}>
            {label && (
              <FormLabel
                htmlFor={name}
                className={cn(
                  'text-sm font-medium',
                  disabled ? 'text-typo-gray-quaternary' : ''
                )}
              >
                {label}
              </FormLabel>
            )}
            {disabled ? (
              <div className="cursor-pointer border-none text-typo-gray-secondary">
                {currentValue
                  ? options.find((opt) => opt.value === currentValue)?.label
                  : '-'}
              </div>
            ) : (
              <FormControl>
                <RadioGroup
                  className="flex gap-4"
                  value={currentValue}
                  onValueChange={field.onChange}
                >
                  {options.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center space-x-2"
                    >
                      <RadioGroupItem
                        id={`${name}-${option.value}`}
                        value={option.value}
                      />
                      <Label
                        htmlFor={`${name}-${option.value}`}
                        className="cursor-pointer font-normal"
                      >
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export default FormCustomRadioGroup;
